import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { HomepagePageCombinedState, HomepagePagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { HomepagePageDataContextProvider } from './pageData';
import { HomepageTrendingSectionDataContextProvider } from './pageTrendingSectionData';

export type ErrorHandlerProps = { context: ErrorStateDetails<HomepagePageCombinedState> };

type Props = {
	prefetchedData: HomepagePagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function HomepagePageContextProvider({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, trendingSectionData } = pageContextState;

	return (
		<HomepagePageDataContextProvider pageData={pageData}>
			<HomepageTrendingSectionDataContextProvider trendingSectionData={trendingSectionData}>
				<TrackingContext properties={{ version: 'v3' }}>{children}</TrackingContext>
			</HomepageTrendingSectionDataContextProvider>
		</HomepagePageDataContextProvider>
	);
}
